"use client";

/**
 * Общий стейт поиска для главной.
 * - На входе (mode "all"): статичный автопарк по ВСЕМ городам, перемешанный
 *   (как на сайтах Солнышко). Публичного эндпоинта «все машины» нет — грузим
 *   /fleet всех городов параллельно, дедупим по модели+году (park-режим
 *   Солнышка) и перемешиваем, чтобы города шли вперемешку. Пагинация —
 *   кнопкой «Показать ещё» (в CarResults).
 * - Выбрал город (mode "fleet"): автопарк этого города.
 * - «Найти машину» с датами (mode "search"): свободные машины с ценой за период.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { fetchCities, fetchFleet, searchCars, mapCar, type ApiCar, type ApiCity, type SearchParams } from "@/lib/api";
import type { CityStat, RentBannerWithCity } from "@/lib/fleet-server";
import type { Car } from "./CarCard";
import type { RentSiteClass } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";
import type { Locale } from "@/lib/locale-utils";

type Status = "loading" | "ready" | "empty" | "error";
type Mode = "all" | "fleet" | "search";

// Карточка класса для блока «Популярные автомобили»: настройки отображения
// (label/image/order) + живые агрегаты по текущему набору машин (мин. цена,
// места и багаж — у самой доступной машины класса).
export type ClassCard = {
  key: string;       // строка класса из 1С — ключ фильтра
  label: string;
  image: string;
  priceFrom: string; // «2 000 ₽» в валюте текущего набора
  seats: number;
  bags: number;
  count: number;
};

// Фильтр каталога (инлайн-пикер, как на сайтах городов). Тип авто = класс
// (карточки «Популярные автомобили»), плюс марка, модель и топливо.
export type Filters = { brand: string | null; model: string | null; fuel: string | null; drive: string | null; models?: string[] | null };

export function carBrand(title: string): string { return (title || "").trim().split(/\s+/)[0]; }

type SearchCtx = {
  cities: ApiCity[];
  activeCity: ApiCity | null;
  cars: Car[];                // отфильтрованные (класс + фильтры) — для каталога
  allCars: Car[];             // весь текущий набор (для подсчёта в модалке фильтров)
  status: Status;
  mode: Mode;
  classes: ClassCard[];       // карточки классов с агрегатами текущего набора
  activeClass: string | null; // выбранный класс-фильтр (null — все)
  filters: Filters;           // марка/модель/топливо
  lastSearch: (SearchParams & { days: number }) | null; // параметры последнего поиска (даты/время) — для инлайн-брони в списке
  cityCards: CityStat[];      // карусель городов на главной (картинка/кол-во/«от N»)
  selectCity: (c: ApiCity | null) => void; // null — назад ко всему автопарку
  runSearch: (p: SearchParams & { days: number }) => void;
  setClass: (cls: string | null) => void;
  setFilters: (f: Filters) => void;
  lockedCity: boolean;          // страница города: город фиксирован → в поиске выбираем место подачи
  pickupGuid: string;           // выбранное место подачи (для брони на странице города)
  setPickupGuid: (g: string) => void;
  banners: RentBannerWithCity[]; // баннеры в парке (главная — все города, город — свои)
};

// Проходит ли машина под выбранный класс + фильтры.
export function matchCar(c: Car, activeClass: string | null, f: Filters): boolean {
  if (activeClass && c.carClass !== activeClass) return false;
  if (f.brand && carBrand(c.title).toUpperCase() !== f.brand) return false;
  // Подборка моделей (из баннера) — машина проходит, если её название совпадает
  // с любой из выбранных моделей (точно или с этого названия начинается).
  if (f.models && f.models.length) {
    const t = (c.title || "").trim().toLowerCase();
    const hit = f.models.some((m) => { const mm = (m || "").trim().toLowerCase(); return !!mm && (t === mm || t.startsWith(mm + " ") || t.startsWith(mm)); });
    if (!hit) return false;
  } else if (f.model && (c.title || "").trim() !== f.model) return false;
  if (f.fuel && (c.features.fuel || "") !== f.fuel) return false;
  if (f.drive && (c.driveUnit || "") !== f.drive) return false;
  return true;
}

// Агрегаты по классам из текущего набора машин + порядок/название/картинка из
// настроек админки. Мин. цена — минимум по классу; места и багаж — самое частое
// значение (мода), а не у одной машины: в классе разные комплектации, типичное
// значение информативнее.
function modeOf(m: Map<number, number>): number {
  let best = 0, bestN = -1;
  for (const [v, n] of m) if (n > bestN) { bestN = n; best = v; }
  return best;
}

// «Умная выдача»: первые 9 карточек должны содержать минивэн, внедорожник,
// бизнес, кабриолет и гибрид (если такие есть в текущем списке) — самый дешёвый
// представитель каждой категории поднимается в топ, вытесняя самые дорогие
// «обычные» карточки. Внутри топ-9 и после него — от дешёвых к дорогим.

// Детерминированное перемешивание топа: сид из guid-состава списка, чтобы
// SSR и клиент дали одинаковый порядок (Math.random() ломал бы гидратацию).
// Порядок меняется, когда меняется набор машин (другие даты/фильтры/город).
function seededShuffle(arr: Car[], seedStr: string): Car[] {
  let seed = 0;
  for (let i = 0; i < seedStr.length; i++) seed = (seed * 31 + seedStr.charCodeAt(i)) >>> 0;
  const rnd = () => {
    seed = (seed + 0x6D2B79F5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function smartOrder(list: Car[]): Car[] {
  const priceOf = (c: Car) => (c.priceKopecks && c.priceKopecks > 0 ? c.priceKopecks : Number.MAX_SAFE_INTEGER);
  const sorted = [...list].sort((a, b) => priceOf(a) - priceOf(b));
  const TOP = 9;
  if (sorted.length <= TOP) return sorted;
  const cats: ((c: Car) => boolean)[] = [
    (c) => /минивэн|минивен|minivan/i.test(c.carClass || ""),
    (c) => /внедорож|jeep/i.test(c.carClass || ""),
    (c) => /бизнес|business/i.test(c.carClass || ""),
    (c) => /кабриол|cabrio|convertible/i.test(c.carClass || ""),
    (c) => !!c.isHybrid,
  ];
  const top = sorted.slice(0, TOP);
  const rest = sorted.slice(TOP);
  const picked: Car[] = [];
  for (const match of cats) {
    if (top.some(match) || picked.some(match)) continue;
    const cand = rest.find((c) => !picked.includes(c) && match(c));
    if (cand) picked.push(cand);
  }
  const seedStr = sorted.map((c) => c.id || "").join("|");
  if (picked.length === 0) {
    return [...seededShuffle(top, seedStr), ...rest];
  }
  const isCat = (c: Car) => cats.some((m) => m(c));
  const keep = [...top];
  let need = picked.length;
  for (let i = keep.length - 1; i >= 0 && need > 0; i--) {
    if (!isCat(keep[i])) { keep.splice(i, 1); need--; }
  }
  while (need > 0 && keep.length > 0) { keep.pop(); need--; }
  const newTop = seededShuffle([...keep, ...picked], seedStr);
  const inTop = new Set(newTop);
  return [...newTop, ...sorted.filter((c) => !inTop.has(c))];
}

function buildClassCards(cars: Car[], settings: RentSiteClass[], locale: Locale): ClassCard[] {
  // Валюта набора: считаем «от N» в доминирующей валюте (на главной — ₽, в
  // выбранном городе — его валюта), иначе самая дешёвая пхукетская машина (฿)
  // подменяла бы цену класса в чужой валюте.
  const curCount = new Map<string, number>();
  for (const c of cars) { const cur = c.currencySym ?? "₽"; curCount.set(cur, (curCount.get(cur) ?? 0) + 1); }
  let domCur = "₽", domN = -1;
  for (const [cur, n] of curCount) if (n > domN) { domN = n; domCur = cur; }

  type Agg = { minDom: number; minAny: number; anyCur: string; count: number; seatFreq: Map<number, number>; bagFreq: Map<number, number> };
  const agg = new Map<string, Agg>();
  for (const c of cars) {
    const k = c.carClass;
    if (!k) continue;
    let e = agg.get(k);
    if (!e) { e = { minDom: Infinity, minAny: Infinity, anyCur: domCur, count: 0, seatFreq: new Map(), bagFreq: new Map() }; agg.set(k, e); }
    e.count++;
    const p = c.priceKopecks ?? 0;
    const cur = c.currencySym ?? "₽";
    if (p > 0) {
      if (cur === domCur && p < e.minDom) e.minDom = p;
      if (p < e.minAny) { e.minAny = p; e.anyCur = cur; }
    }
    const s = c.seatsNum ?? 0;
    if (s > 0) e.seatFreq.set(s, (e.seatFreq.get(s) ?? 0) + 1);
    const b = c.bagsNum ?? 0;
    e.bagFreq.set(b, (e.bagFreq.get(b) ?? 0) + 1);
  }
  const ordered = [...settings].sort((a, b) => a.sort - b.sort);
  const cards: ClassCard[] = [];
  for (const s of ordered) {
    if (s.hidden) continue;
    const e = agg.get(s.class);
    if (!e || e.count === 0) continue; // нет машин класса в текущем наборе
    // цена в доминирующей валюте, если она есть; иначе — в валюте самой дешёвой машины
    const useDom = e.minDom !== Infinity;
    const minK = useDom ? e.minDom : e.minAny;
    const cur = useDom ? domCur : e.anyCur;
    const minRub = minK === Infinity ? 0 : Math.round(minK / 100);
    cards.push({
      key: s.class,
      label: (locale === "en" && s.label_en) ? s.label_en : (s.label || s.class),
      image: s.image_url || "",
      priceFrom: `${minRub.toLocaleString("ru-RU")} ${cur}`,
      seats: modeOf(e.seatFreq),
      bags: modeOf(e.bagFreq),
      count: e.count,
    });
  }
  return cards;
}

const Ctx = createContext<SearchCtx | null>(null);

export function useSearch(): SearchCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSearch must be used within SearchProvider");
  return v;
}

// Fisher–Yates: перемешиваем машины, чтобы города в общем автопарке шли вперемешку.
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Одна карточка на модель В ПРЕДЕЛАХ ОФИСА: марка/модель/год + офис. В городе с
// несколькими офисами одинаковые машины в РАЗНЫХ офисах — отдельные карточки
// (поиск + автопарк выбранного города). В одном офисе одинаковые — одна.
function dedupeByModel(list: ApiCar[]): ApiCar[] {
  const seen = new Set<string>();
  const out: ApiCar[] = [];
  for (const x of list) {
    const key = `${(x.name || "").toLowerCase().trim()}__${x.yearOfManufacture || 0}__${(x.officeName || "").toLowerCase().trim()}`;
    if (x.guid && !seen.has(key)) { seen.add(key); out.push(x); }
  }
  return out;
}

export default function SearchProvider({
  children, initialCities, initialCars, initialClasses, initialCityStats, initialActiveCity, initialBanners,
}: {
  children: ReactNode;
  initialCities?: ApiCity[];
  initialCars?: Car[];
  initialClasses?: RentSiteClass[];
  initialCityStats?: CityStat[];
  initialActiveCity?: ApiCity; // страница города: стартуем с выбранным городом (режим «fleet»)
  initialBanners?: RentBannerWithCity[];
}) {
  // Автопарк пришёл с сервера (SSR). На главной — режим «all» (весь парк); на
  // странице города — режим «fleet» с этим городом активным.
  const hasInitial = !!(initialCars && initialCars.length);
  const [cities, setCities] = useState<ApiCity[]>(initialCities ?? []);
  const [activeCity, setActiveCity] = useState<ApiCity | null>(initialActiveCity ?? null);
  const [cars, setCars] = useState<Car[]>(initialCars ?? []);
  const [status, setStatus] = useState<Status>((initialCars && initialCars.length) ? "ready" : "loading");
  const [mode, setMode] = useState<Mode>(initialActiveCity ? "fleet" : "all");
  // Карусель городов (картинка/кол-во/«от N») — статично с сервера.
  const [cityCards] = useState<CityStat[]>(initialCityStats ?? []);
  // Кэш «всего автопарка» — чтобы вернуться к нему по клику на активный город без перезагрузки.
  const allFleetRef = useRef<Car[]>(initialCars ?? []);
  // настройки классов (название/картинка/порядок/скрытие) — из SSR или дозагрузка.
  const [classSettings, setClassSettings] = useState<RentSiteClass[]>(initialClasses ?? []);
  const [activeClass, setActiveClass] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<Filters>({ brand: null, model: null, fuel: null, drive: null });
  const [lastSearch, setLastSearch] = useState<(SearchParams & { days: number }) | null>(null);
  const lockedCity = !!initialActiveCity; // страница города
  const [pickupGuid, setPickupGuid] = useState("");
  const [banners] = useState<RentBannerWithCity[]>(initialBanners ?? []);
  // токен поколения: выбор города / поиск отменяет ещё не завершённую загрузку «всех».
  const genRef = useRef(0);

  // Настройки классов не пришли с сервера — дозагружаем на клиенте.
  useEffect(() => {
    if (classSettings.length) return;
    fetch("/api/v1/rent-site/classes", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : { classes: [] }))
      .then((d) => setClassSettings(d.classes || []))
      .catch(() => {});
  }, []);

  // Карточки классов (агрегаты текущего набора) и каталог, отфильтрованный по классу.
  const { locale } = useLocale();
  const classes = useMemo(() => buildClassCards(cars, classSettings, locale), [cars, classSettings, locale]);
  const displayCars = useMemo(
    () => smartOrder(cars.filter((c) => matchCar(c, activeClass, filters))),
    [cars, activeClass, filters],
  );
  const setClass = (cls: string | null) => setActiveClass(cls); // точечно; тоггл — на стороне карточки
  const setFilters = (f: Filters) => setFiltersState(f);

  // Автопарк уже пришёл с сервера (SSR) — ничего не грузим, показываем сразу.
  // Иначе (фолбэк) — собираем общий автопарк на клиенте, как раньше.
  useEffect(() => {
    if (hasInitial) return;
    const gen = ++genRef.current;
    setMode("all"); setStatus("loading"); setCars([]);
    fetchCities()
      .then(async (cs) => {
        if (genRef.current !== gen) return;
        setCities(cs);
        const lists = await Promise.all(
          cs.map((c) =>
            fetchFleet(c.station)
              .then((cars) => ({ cars, city: c }))
              .catch(() => ({ cars: [] as ApiCar[], city: c })),
          ),
        );
        if (genRef.current !== gen) return;
        // дедуп по модели+году (один вариант на модель), несём город/валюту.
        const seen = new Set<string>();
        const uniq: { car: ApiCar; city: ApiCity }[] = [];
        for (const { cars, city } of lists) {
          for (const x of cars) {
            const key = `${(x.name || "").toLowerCase().trim()}__${x.yearOfManufacture || 0}`;
            if (x.guid && !seen.has(key)) { seen.add(key); uniq.push({ car: x, city }); }
          }
        }
        const mixed = shuffle(uniq).map(({ car, city }) => ({
          ...mapCar(car, "fleet", undefined, city.currency || "₽"),
          city: city.name,
          station: city.station,
        }));
        setCars(mixed);
        setStatus(mixed.length ? "ready" : "empty");
      })
      .catch(() => { if (genRef.current === gen) setStatus("error"); });
  }, []);

  const selectCity = (c: ApiCity | null) => {
    genRef.current++; // отменяем фоновую загрузку «всех»
    setActiveClass(null); setFiltersState({ brand: null, model: null, fuel: null, drive: null }); // набор меняется — сбрасываем фильтры
    if (!c) {
      // Назад ко всему автопарку — из кэша SSR, без перезагрузки.
      setActiveCity(null); setMode("all");
      setCars(allFleetRef.current);
      setStatus(allFleetRef.current.length ? "ready" : "empty");
      return;
    }
    setActiveCity(c); setMode("fleet"); setStatus("loading"); setCars([]);
    const cur = c.currency || "₽";
    fetchFleet(c.station)
      .then((list) => {
        // одна карточка на модель (дедуп по модели+году), помечаем городом.
        // Филиал в автопарке не показываем — 1С его в GetCars не даёт (mapCar).
        const uniq = dedupeByModel(list);
        setCars(uniq.map((x) => ({ ...mapCar(x, "fleet", undefined, cur), city: c.name, station: c.station })));
        setStatus(uniq.length ? "ready" : "empty");
      })
      .catch(() => setStatus("error"));
  };

  const runSearch = (p: SearchParams & { days: number }) => {
    genRef.current++;
    setLastSearch(p);
    setActiveClass(null); setFiltersState({ brand: null, model: null, fuel: null, drive: null });
    setMode("search"); setStatus("loading"); setCars([]);
    const cityObj = cities.find((c) => c.station === p.station) || activeCity;
    const cur = cityObj?.currency || "₽";
    // Филиал на «свободных» приходит из 1С (officeName) — mapCar кладёт его в filial.
    // Выбранное место подачи ограничивает выдачу его филиалом (тот же филиал = те же машины).
    searchCars({ ...p, deliveryGuid: pickupGuid || undefined })
      .then((list) => {
        setCars(list.map((x) => ({ ...mapCar(x, "search", p.days, cur), city: cityObj?.name, station: p.station })));
        setStatus(list.length ? "ready" : "empty");
      })
      .catch(() => setStatus("error"));
  };

  return (
    <Ctx.Provider value={{ cities, activeCity, cars: displayCars, allCars: cars, status, mode, classes, activeClass, filters, lastSearch, cityCards, selectCity, runSearch, setClass, setFilters, lockedCity, pickupGuid, setPickupGuid, banners }}>
      {children}
    </Ctx.Provider>
  );
}
